import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Tabs, Tab, Card, CardContent, Typography, Button, IconButton, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Grid, TablePagination, InputAdornment, Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  LocalShipping as ShipIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Block as VoidIcon,
  Undo as UnshipIcon,
  DoneAll as DoneIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import ReadyToInvoice from './ReadyToInvoice';
import {
  listPendingShipOrders,
  listVoidedWholesaleOrders,
  listShippedWholesaleOrders,
  shipWholesaleOrder,
  voidWholesaleOrder,
  unvoidWholesaleOrder,
  unshipWholesaleOrder
} from '../../services/retailOrdersService';
import api from '../../config/api';
import { PageHeader } from '../ds';
import { useUrlState } from '../../hooks/useUrlState';

// Matches the convention in retailOrdersService — pass an absolute URL so the
// axios baseURL is overridden (the dismiss endpoint isn't in that service file
// because it lives outside this bot's editable scope, so we call it inline).
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

/**
 * Pending Ship — cross-brand list of wholesale orders awaiting shipment.
 * Each row has a Ship action that fulfills inventory, locks FX, and writes
 * an order_fulfillments row. Orders then move to the Ready-to-Invoice queue.
 */
function PendingShipQueue() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // URL-bound state so browser-back from CustomerHistory restores filters.
  const [search, setSearch] = useUrlState('q', '');
  const [page, setPage] = useUrlState('p', 0, { parse: (v) => parseInt(v, 10) || 0 });
  const [rowsPerPage, setRowsPerPage] = useUrlState('pp', 25, { parse: (v) => parseInt(v, 10) || 25 });
  const [view, setView] = useUrlState('view', 'pending'); // 'pending' | 'shipped' | 'voided'
  const skipFirstPageReset = useRef(true);

  const [shipOpen, setShipOpen] = useState(false);
  const [shipTarget, setShipTarget] = useState(null);
  const [shippedDate, setShippedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [tracking, setTracking] = useState('');
  const [carrier, setCarrier] = useState('');
  const [saving, setSaving] = useState(false);

  // Void dialog — captures the audit-trail reason
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);

  // Restore (un-void) dialog — operator-driven reversal of a void. UI calls
  // this "Restore" (Shopify/QB term) but the backend endpoint is /unvoid for
  // symmetry with /void. Reason captured for the audit trail.
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreReason, setRestoreReason] = useState('');
  const [restoring, setRestoring] = useState(false);

  // Unship dialog — reverses the LATEST shipment for an already-shipped or
  // partially-shipped order, returning it to pending/partially_shipped without
  // voiding. Used from the Shipped tab when the operator needs to correct one
  // shipment (wrong qty, wrong warehouse) but keep the order alive.
  const [unshipOpen, setUnshipOpen] = useState(false);
  const [unshipTarget, setUnshipTarget] = useState(null);
  const [unshipReason, setUnshipReason] = useState('');
  const [unshipping, setUnshipping] = useState(false);

  // "Done — remove from Pending Ship" — for partially-shipped orders where the
  // operator has decided not to ship the remaining line(s). Drops the order out
  // of the queue WITHOUT marking it shipped (which would wrongly deduct the
  // un-shipped lines from on-hand stock). Reversible from the Shipped tab.
  const [doneOpen, setDoneOpen] = useState(false);
  const [doneTarget, setDoneTarget] = useState(null);
  const [doneSaving, setDoneSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      let r;
      if (view === 'voided') r = await listVoidedWholesaleOrders();
      else if (view === 'shipped') r = await listShippedWholesaleOrders();
      else r = await listPendingShipOrders();
      setOrders(r.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [view]);

  const filtered = useMemo(() => {
    if (!search.trim()) return orders;
    const q = search.trim().toLowerCase();
    return orders.filter(o =>
      (o.order_id || '').toLowerCase().includes(q) ||
      (o.order_name || '').toLowerCase().includes(q) ||
      (o.customer_name || '').toLowerCase().includes(q) ||
      (o.customer_id || '').toLowerCase().includes(q) ||
      (o.po_number || '').toLowerCase().includes(q) ||
      (o.brand_name || '').toLowerCase().includes(q)
    );
  }, [orders, search]);

  // Reset to first page when filters change — but skip the very first run so
  // hydrated `?p=N` from the URL isn't wiped on mount.
  useEffect(() => {
    if (skipFirstPageReset.current) { skipFirstPageReset.current = false; return; }
    setPage(0);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps
  const paged = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  // Per-line ship state for partial-ship support. shipLines is an array of
  // { sku, product_name, qty_dispatched, qty_to_ship } seeded from the order's
  // products_info on dialog open. Operator can edit qty_to_ship down per line.
  const [shipLines, setShipLines] = useState([]);
  const [shipWarehouse, setShipWarehouse] = useState('');

  const openShip = (order) => {
    setShipTarget(order);
    setShippedDate(new Date().toISOString().slice(0, 10));
    setTracking('');
    setCarrier('');
    setShipWarehouse(order.warehouse_code || '');
    let raw = [];
    try {
      const parsed = typeof order.products_info === 'string'
        ? JSON.parse(order.products_info) : (order.products_info || []);
      raw = Array.isArray(parsed) ? parsed : [];
    } catch (_) { raw = []; }
    // Note: products_info doesn't carry "already shipped" — the backend
    // computes remaining from order_fulfillments at submit. Frontend just
    // defaults qty_to_ship = quantity_dispatched and lets the backend cap it.
    // CIN7-sourced lines use sku/name/qty/qty_shipped (not product_sku/
    // quantity_dispatched/quantity), so the native-only read seeded empty SKU
    // + 0 qty and the modal showed "Dispatched 0 / Ship 0" (Rishabh 2026-06-02,
    // OHAI2601-44 / EINC2564-10). Normalize both shapes, matching the backend
    // shipWholesaleOrder fix: native keeps quantity_dispatched ?? quantity;
    // CIN7 falls back qty_shipped>0 ? qty_shipped : qty (?? skips null, not 0,
    // and CIN7 reports qty_shipped=0 until it dispatches — so ship the ordered qty).
    const shipQty = p => Number(p.quantity_dispatched ?? p.quantity ?? (Number(p.qty_shipped) || Number(p.qty) || 0));
    // Default the ship qty from the operator's internal allocation (Allocate
    // column on the connector order) ONLY when one is actually filled
    // (allocated_quantity present + > 0); otherwise fall back to the dispatched
    // qty (Rishabh 2026-06-04). Capped at dispatched — the backend ship path
    // hard-caps at the dispatched qty, so we never default above it. A partial
    // allocation (< dispatched) flows through the existing partial-ship path:
    // it ships the allocated qty and leaves the remainder in Pending Ship.
    setShipLines(raw.map(p => {
      const disp = shipQty(p);
      const alloc = Number(p.allocated_quantity);
      const shipDefault = (Number.isFinite(alloc) && alloc > 0) ? Math.min(alloc, disp) : disp;
      return {
        sku: p.product_sku || p.sku || '',
        product_name: p.product_name || p.name || '',
        qty_dispatched: disp,
        qty_to_ship: shipDefault,
        // Per-line ship-from warehouse the operator set in the Allocate sidebar
        // (empty = ship from the order-level "From Warehouse"). Sent with the ship
        // payload so the backend deducts this line from THIS warehouse.
        alloc_warehouse: p.allocated_warehouse_code || '',
      };
    }));
    setError(null); setShipOpen(true);
  };

  const submitShip = async () => {
    if (!shippedDate) { setError('Shipped date required'); return; }
    if (!shipLines.length || shipLines.every(l => Number(l.qty_to_ship) === 0)) {
      setError('Set at least one line\'s ship qty above 0'); return;
    }
    try {
      setSaving(true);
      // Send items: array when any line is shipping LESS than dispatched
      // (partial), otherwise let backend take the default fast path.
      const isPartial = shipLines.some(l => Number(l.qty_to_ship) !== Number(l.qty_dispatched));
      const body = {
        shipped_date: shippedDate,
        tracking_number: tracking,
        carrier: carrier,
        warehouse_code: shipWarehouse || undefined,
      };
      if (isPartial) {
        body.items = shipLines
          .filter(l => Number(l.qty_to_ship) > 0)
          .map(l => ({
            sku: l.sku,
            quantity_shipped: Number(l.qty_to_ship),
            // Carry the per-line warehouse so a split order deducts each line from
            // its own warehouse. Omitted when unset → backend falls back to the
            // saved allocation, then the order-level warehouse.
            ...(l.alloc_warehouse ? { allocated_warehouse_code: l.alloc_warehouse } : {}),
          }));
      }
      const r = await shipWholesaleOrder(shipTarget.brand_id, shipTarget.order_id, body);
      const msg = r.is_partial
        ? `Partial shipment of ${shipTarget.order_id} — ${r.remaining_after} units still pending`
        : `Shipped ${shipTarget.order_id}. FX locked ${r.fx_lock?.from_currency}→${r.fx_lock?.to_currency} @ ${r.fx_lock?.rate}.`;
      setSuccess(msg);
      setShipOpen(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const openDone = (order) => {
    setDoneTarget(order);
    setError(null); setDoneOpen(true);
  };
  const submitDone = async () => {
    if (!doneTarget) return;
    try {
      setDoneSaving(true);
      await api.post(
        `${API_BASE}/api/cogs/brands/${doneTarget.brand_id}/retail-orders/${encodeURIComponent(doneTarget.order_id)}/pending-ship-dismiss`,
        { dismissed: true }
      );
      const label = (doneTarget.order_id || '').replace(/^CIN7-/, '');
      setSuccess(`${label} removed from Pending Ship. Records unchanged — restore it any time from the Shipped tab.`);
      setDoneOpen(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setDoneSaving(false);
    }
  };
  // Reverse a dismissal — puts the order back in the Pending Ship queue.
  const restoreToPending = async (order) => {
    try {
      await api.post(
        `${API_BASE}/api/cogs/brands/${order.brand_id}/retail-orders/${encodeURIComponent(order.order_id)}/pending-ship-dismiss`,
        { dismissed: false }
      );
      const label = (order.order_id || '').replace(/^CIN7-/, '');
      setSuccess(`${label} restored to Pending Ship.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const openVoid = (order) => {
    setVoidTarget(order);
    setVoidReason('');
    setError(null); setVoidOpen(true);
  };
  const submitVoid = async () => {
    if (!voidReason.trim()) { setError('A reason is required to void (audit trail).'); return; }
    try {
      setVoiding(true);
      const orderLabel = (voidTarget.order_id || '').replace(/^CIN7-/, '');
      const isShipped = ['shipped', 'partially_shipped'].includes(voidTarget.fulfillment_status);

      if (isShipped) {
        // Shipped/partially_shipped order: loop unshipWholesaleOrder until the
        // response says new_status='pending', then chain voidWholesaleOrder.
        // The backend /void endpoint rejects shipped orders (409) so we have
        // to reverse every fulfillment first. Cap at 20 iterations for
        // safety. Matches the loop in inventory/RetailOrders.js submitVoid.
        const reasonTrim = voidReason.trim();
        const MAX_UNSHIPS = 20;
        let r = null;
        let unshipsDone = 0;
        let stopReason = null;

        while (unshipsDone < MAX_UNSHIPS) {
          try {
            r = await unshipWholesaleOrder(voidTarget.brand_id, voidTarget.order_id, reasonTrim);
          } catch (e) {
            const body = e?.response?.data || {};
            stopReason = `${body.error || e?.message || 'unship failed'}${body.detail ? ` — ${body.detail}` : ''}`;
            break;
          }
          unshipsDone += 1;
          const hasPartials = (r?.partial_items?.length || 0) > 0
                           || (r?.missing_batches?.length || 0) > 0;
          if (hasPartials || r?.warning) {
            stopReason = r?.warning || 'partial restore — missing batches need manual review';
            break;
          }
          if (r?.new_status === 'pending') break;
          if (r?.new_status !== 'partially_shipped') {
            stopReason = `unexpected status '${r?.new_status}' after unship`;
            break;
          }
        }

        const fullyUnshipped = r?.new_status === 'pending' && !stopReason;
        if (fullyUnshipped) {
          await voidWholesaleOrder(voidTarget.brand_id, voidTarget.order_id, reasonTrim);
          setSuccess(`Voided ${orderLabel} — ${unshipsDone} shipment${unshipsDone === 1 ? '' : 's'} reversed, inventory restored, order voided.`);
        } else if (unshipsDone >= MAX_UNSHIPS) {
          setError(`${orderLabel}: more than ${MAX_UNSHIPS} prior shipments — too many to auto-cancel. Cancel manually.`);
        } else {
          setError(`${orderLabel}: ${unshipsDone} shipment${unshipsDone === 1 ? '' : 's'} reversed but stopped before void${stopReason ? ` — ${stopReason}` : ''}.`);
        }
      } else {
        // Already pending → direct void.
        await voidWholesaleOrder(voidTarget.brand_id, voidTarget.order_id, voidReason.trim());
        setSuccess(`Voided ${orderLabel} — reason logged to audit trail.`);
      }
      setVoidOpen(false);
      await load();
    } catch (e) {
      const body = e?.response?.data || {};
      const headline = body.error || e?.message || 'Action failed';
      const detail = body.detail ? ` — ${body.detail}` : '';
      setError(`${headline}${detail}`);
    } finally { setVoiding(false); }
  };

  // Unship handlers — single-shipment reversal. Reverses the LATEST shipped
  // fulfillment; order moves to pending or partially_shipped depending on
  // whether older shipments remain. Operator chooses this (rather than full
  // Void) when they want to keep the order alive and just correct one
  // shipment.
  const openUnship = (order) => {
    setUnshipTarget(order);
    setUnshipReason('');
    setError(null); setUnshipOpen(true);
  };
  const submitUnship = async () => {
    if (!unshipReason.trim()) { setError('A reason is required to unship (audit trail).'); return; }
    try {
      setUnshipping(true);
      const orderLabel = (unshipTarget.order_id || '').replace(/^CIN7-/, '');
      const r = await unshipWholesaleOrder(unshipTarget.brand_id, unshipTarget.order_id, unshipReason.trim());
      const hasPartials = (r?.partial_items?.length || 0) > 0
                       || (r?.missing_batches?.length || 0) > 0;
      if (r?.warning || hasPartials) {
        setError(`${orderLabel}: ${r?.warning || 'partial restore — missing batches need manual review'}. Order is now ${r?.new_status || 'unknown'}.`);
      } else {
        setSuccess(`Unshipped ${orderLabel} — latest shipment reversed, inventory restored. Order is now ${r?.new_status || 'pending'}.`);
      }
      setUnshipOpen(false);
      await load();
    } catch (e) {
      const body = e?.response?.data || {};
      const headline = body.error || e?.message || 'Action failed';
      const detail = body.detail ? ` — ${body.detail}` : '';
      setError(`${headline}${detail}`);
    } finally { setUnshipping(false); }
  };

  // Restore (un-void) handlers — mirrors void. Reason mandatory for the audit
  // trail. Backend gates: already-voided, NOT invoiced, source matches. If
  // inventory has been consumed since the void, backend returns 409 with a
  // specific shortage message — surface inline rather than masking it.
  const openRestore = (order) => {
    setRestoreTarget(order);
    setRestoreReason('');
    setError(null); setRestoreOpen(true);
  };
  const submitRestore = async () => {
    if (!restoreReason.trim()) { setError('A reason is required to restore (audit trail).'); return; }
    try {
      setRestoring(true);
      await unvoidWholesaleOrder(restoreTarget.brand_id, restoreTarget.order_id, restoreReason.trim());
      setSuccess(`Restored ${(restoreTarget.order_id || '').replace(/^CIN7-/, '')} — order is now pending again.`);
      setRestoreOpen(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setRestoring(false); }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <PageHeader
        primary={view === 'voided' ? 'Voided Orders' : view === 'shipped' ? 'Shipped Orders' : 'Pending Ship'}
        metadata={[{
          label: view === 'voided'
            ? 'Soft-voided wholesale orders. Stock holds released; audit trail preserved.'
            : view === 'shipped'
              ? 'Wholesale orders already shipped. Includes partials so you can find any order that left the queue.'
              : 'Wholesale orders awaiting shipment. Ship fulfills inventory, locks FX, writes order_fulfillment.',
        }]}
        actions={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label="Pending"
              color={view === 'pending' ? 'primary' : 'default'}
              variant={view === 'pending' ? 'filled' : 'outlined'}
              onClick={() => setView('pending')}
              size="small"
            />
            <Chip
              label="Shipped"
              color={view === 'shipped' ? 'success' : 'default'}
              variant={view === 'shipped' ? 'filled' : 'outlined'}
              onClick={() => setView('shipped')}
              size="small"
            />
            <Chip
              label="Voided"
              color={view === 'voided' ? 'error' : 'default'}
              variant={view === 'voided' ? 'filled' : 'outlined'}
              onClick={() => setView('voided')}
              size="small"
            />
            <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
          </Box>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              {filtered.length} order{filtered.length === 1 ? '' : 's'}
              {view === 'voided' ? ' voided' : view === 'shipped' ? ' shipped' : ' pending'}
            </Typography>
            <TextField
              size="small"
              placeholder="Search order #, customer, PO, brand…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ width: 320 }}
            />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Brand</TableCell>
                  <TableCell>Order #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>PO #</TableCell>
                  <TableCell>From Warehouse</TableCell>
                  <TableCell align="right">Value</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center">
                    <Typography color="textSecondary" sx={{ py: 2 }}>
                      {view === 'voided' ? 'No voided orders.' : view === 'shipped' ? 'No shipped orders.' : 'No orders pending ship.'}
                    </Typography>
                  </TableCell></TableRow>
                ) : paged.map(o => (
                  <TableRow key={`${o.brand_id}-${o.order_id}`} hover>
                    <TableCell><Chip label={o.brand_name} size="small" /></TableCell>
                    <TableCell>
                      {(() => {
                        // Hover tooltip: thumbnail + product name + SKU + dispatched qty.
                        // Shortfall (qty_ordered − qty_dispatched) shown as a compact red chip
                        // only when present. Image / name come from the backend's
                        // product_master enrichment on products_info.
                        let prods = [];
                        try {
                          const raw = typeof o.products_info === 'string'
                            ? JSON.parse(o.products_info) : (o.products_info || []);
                          prods = Array.isArray(raw) ? raw : [];
                        } catch (_) { prods = []; }
                        const tipBody = prods.length === 0 ? (
                          <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(no line items)</Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 280, maxWidth: 380 }}>
                            {prods.map((p, idx) => {
                              const qo = Number(p.quantity_ordered ?? p.quantity ?? 0);
                              const qd = Number(p.quantity_dispatched ?? p.quantity ?? qo);
                              const short = qo - qd;
                              const img = p.thumbnail_url || p.image_url || '';
                              return (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {img ? (
                                    <Box component="img" src={img} alt={p.product_sku || ''}
                                      sx={{ width: 36, height: 36, borderRadius: 0.5, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.2)', flexShrink: 0, bgcolor: 'rgba(255,255,255,0.05)' }}
                                      onError={(e) => { e.target.style.visibility = 'hidden'; }} />
                                  ) : (
                                    <Box sx={{ width: 36, height: 36, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,0.08)', flexShrink: 0,
                                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'rgba(255,255,255,0.5)' }}>
                                      no img
                                    </Box>
                                  )}
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, fontSize: 12, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {p.product_name || p.product_sku || 'Unknown product'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: 10, opacity: 0.7 }}>
                                      {p.product_sku || '—'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 13 }}>
                                      × {qd}
                                    </Typography>
                                    {short > 0 && (
                                      <Box sx={{ fontSize: 9, px: 0.5, py: 0.1, borderRadius: 0.5, bgcolor: 'warning.main', color: 'warning.contrastText', fontWeight: 600, lineHeight: 1.4 }}>
                                        short {short}
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        );
                        const orderIdStripped = (o.order_id || '').replace(/^CIN7-/, '');
                        const orderName = o.order_name || '';
                        const showBoth = orderName && orderName !== orderIdStripped;
                        return (
                          <Tooltip
                            arrow placement="right"
                            title={tipBody}
                            componentsProps={{ tooltip: { sx: { bgcolor: 'grey.900', maxWidth: 'none', p: 1.25 } } }}>
                            <Box sx={{ cursor: 'help', display: 'inline-block', borderBottom: '1px dotted #aaa' }}>
                              <strong>{showBoth ? orderName : orderIdStripped}</strong>
                              {showBoth && (
                                <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: 'text.disabled', fontFamily: 'monospace', lineHeight: 1 }}>
                                  {orderIdStripped}
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell>{o.event_date}</TableCell>
                    <TableCell>
                      {o.customer_id ? (
                        <RouterLink to={`/retail/customers/${encodeURIComponent(o.customer_id)}/history`}
                          style={{ color: 'inherit', textDecoration: 'underline dotted' }}>
                          {o.customer_name || o.customer_id}
                        </RouterLink>
                      ) : (o.customer_name || '—')}
                    </TableCell>
                    <TableCell>{o.po_number || '-'}</TableCell>
                    <TableCell>{o.warehouse_code || '-'}</TableCell>
                    <TableCell align="right">{o.currency} {Number(o.grand_total ?? o.order_revenue).toFixed(2)}</TableCell>
                    <TableCell align="right">
                      {view === 'voided' ? (
                        <Box sx={{ display: 'inline-flex', gap: 0.75, alignItems: 'center' }}>
                          <Chip label="Voided" color="error" size="small" />
                          <Button size="small" variant="outlined"
                            title="Restore this order — re-places inventory hold and returns to Pending"
                            onClick={() => openRestore(o)}>
                            Restore
                          </Button>
                        </Box>
                      ) : view === 'shipped' ? (
                        <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
                          <Chip
                            label={o.fulfillment_status === 'partially_shipped' ? 'Partial' : 'Shipped'}
                            color={o.fulfillment_status === 'partially_shipped' ? 'warning' : 'success'}
                            size="small"
                          />
                          {o.pending_ship_dismissed && (
                            <Tooltip title="Removed from the Pending Ship queue by an operator. Click Restore to put it back.">
                              <Chip label="Off queue" color="default" variant="outlined" size="small" />
                            </Tooltip>
                          )}
                          {o.pending_ship_dismissed && (
                            <Button size="small" variant="outlined"
                              title="Put this order back in the Pending Ship queue"
                              onClick={() => restoreToPending(o)}>
                              Restore
                            </Button>
                          )}
                          <RouterLink
                            to={`/wholesale/new?edit=${encodeURIComponent(o.order_id)}&brand=${o.brand_id}`}
                            style={{ color: 'inherit', textDecoration: 'none' }}>
                            <Button size="small" variant="outlined"
                              title="Open this order (view-only for shipped orders)">
                              Open
                            </Button>
                          </RouterLink>
                          <Button size="small" variant="outlined" startIcon={<UnshipIcon />}
                            title="Reverse the latest shipment — inventory restored, order returns to pending/partial"
                            onClick={() => openUnship(o)}>
                            Unship
                          </Button>
                          <IconButton size="small" color="error"
                            title="Void the order — reverses every shipment then marks voided (requires reason)"
                            onClick={() => openVoid(o)}>
                            <VoidIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
                          {o.fulfillment_status === 'partially_shipped' && (
                            <Chip label="Partially shipped" color="warning" size="small"
                              title="Some lines already shipped; remaining qty still pending. Ship the rest, or click Done if this partial is final." />
                          )}
                          <RouterLink
                            to={`/wholesale/new?edit=${encodeURIComponent(o.order_id)}&brand=${o.brand_id}`}
                            style={{ color: 'inherit', textDecoration: 'none' }}>
                            <IconButton size="small" color="primary"
                              title="Edit this order (opens the wholesale form in edit mode)">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </RouterLink>
                          {o.source === 'wholesale_native' && o.fulfillment_status !== 'partially_shipped' ? (
                            // Manual orders ship from the order page only: Dispatch date
                            // (stock leaves, stock-checked) then optional Delivered date
                            // (Rishabh 2026-09-24). Connector orders (SPS/Procuros) have a
                            // read-only order page, so they still ship from here, as do
                            // remaining qtys of an already partially-shipped order.
                            <RouterLink
                              to={`/wholesale/new?edit=${encodeURIComponent(o.order_id)}&brand=${o.brand_id}`}
                              style={{ color: 'inherit', textDecoration: 'none' }}>
                              <Button size="small" variant="contained" startIcon={<ShipIcon />}
                                title="Set the Dispatch date (and Delivered date) on the order page">
                                Open order
                              </Button>
                            </RouterLink>
                          ) : (
                            <Button size="small" variant="contained" startIcon={<ShipIcon />}
                              onClick={() => openShip(o)}>Ship</Button>
                          )}
                          {o.fulfillment_status === 'partially_shipped' && (
                            <Button size="small" variant="outlined" color="success" startIcon={<DoneIcon />}
                              title="Remove from Pending Ship — use when you've decided not to ship the remaining line(s). Keeps records accurate (no stock change) and is reversible from the Shipped tab."
                              onClick={() => openDone(o)}>
                              Done
                            </Button>
                          )}
                          <IconButton size="small" color="error"
                            title="Void this order (requires reason for audit trail)"
                            onClick={() => openVoid(o)}>
                            <VoidIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </CardContent>
      </Card>

      <Dialog open={shipOpen} onClose={() => setShipOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Ship {(shipTarget?.order_id || '').replace(/^CIN7-/, '')}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {shipTarget && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {shipTarget.customer_name || shipTarget.customer_id} — {shipTarget.currency} {Number(shipTarget.grand_total ?? shipTarget.order_revenue).toFixed(2)}
                {shipTarget.fulfillment_status === 'partially_shipped' && (
                  <Chip label="Partially shipped" color="warning" size="small" sx={{ ml: 1, fontSize: 10, height: 18 }} />
                )}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}>
                  <TextField fullWidth size="small" label="Shipped Date *" type="date" value={shippedDate}
                    onChange={(e) => setShippedDate(e.target.value)}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField fullWidth size="small" label="From Warehouse" value={shipWarehouse}
                    onChange={(e) => setShipWarehouse(e.target.value.toUpperCase())}
                    placeholder={shipTarget.warehouse_code || 'e.g. G3PL'}
                    helperText="Default for lines without a per-line 'Ship From' warehouse"
                    FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField fullWidth size="small" label="Tracking #" value={tracking}
                    onChange={(e) => setTracking(e.target.value)} placeholder="optional" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField fullWidth size="small" label="Carrier" value={carrier}
                    onChange={(e) => setCarrier(e.target.value)} placeholder="UPS / FedEx / DHL …" />
                </Grid>
              </Grid>

              {/* Per-line shipping — operator can lower qty per line to record
                  a PARTIAL shipment. Order stays in Pending Ship with the
                  remaining qty visible after submit. Multiple partials can be
                  recorded over time with different warehouses + dates. */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Lines to ship</Typography>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 2.5fr 1fr 1.2fr 1fr', bgcolor: 'grey.100', px: 1, py: 0.5, fontSize: 11, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                  <span>SKU</span>
                  <span>Product</span>
                  <span style={{ textAlign: 'right' }}>Dispatched</span>
                  <span style={{ textAlign: 'right' }}>Ship From</span>
                  <span style={{ textAlign: 'right' }}>Ship Now</span>
                </Box>
                {shipLines.map((l, idx) => (
                  <Box key={idx} sx={{ display: 'grid', gridTemplateColumns: '2fr 2.5fr 1fr 1.2fr 1fr', alignItems: 'center', px: 1, py: 0.5, borderTop: idx > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{l.sku}</Typography>
                    <Typography variant="body2" sx={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.product_name || '—'}</Typography>
                    <Typography variant="body2" align="right" sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>{l.qty_dispatched}</Typography>
                    <Typography variant="body2" align="right" sx={{ fontFamily: 'monospace', fontSize: 12, color: (l.alloc_warehouse && l.alloc_warehouse !== (shipWarehouse || shipTarget?.warehouse_code)) ? 'primary.main' : 'text.secondary' }}>
                      {l.alloc_warehouse || shipWarehouse || shipTarget?.warehouse_code || '—'}
                    </Typography>
                    <TextField size="small" type="number" value={l.qty_to_ship}
                      inputProps={{ min: 0, max: l.qty_dispatched, style: { textAlign: 'right', fontFamily: 'monospace' } }}
                      onChange={(e) => {
                        const next = [...shipLines];
                        next[idx] = { ...next[idx], qty_to_ship: Math.max(0, Math.min(Number(e.target.value) || 0, l.qty_dispatched)) };
                        setShipLines(next);
                      }}
                      sx={{ '& .MuiInputBase-root': { fontSize: 12 } }} />
                  </Box>
                ))}
              </Box>
              {(() => {
                const totalDispatched = shipLines.reduce((s, l) => s + l.qty_dispatched, 0);
                const totalShipping = shipLines.reduce((s, l) => s + Number(l.qty_to_ship || 0), 0);
                const isPartial = totalShipping > 0 && totalShipping < totalDispatched;
                return (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: isPartial ? 'warning.dark' : 'text.secondary', fontStyle: 'italic' }}>
                    Shipping {totalShipping} of {totalDispatched} units.
                    {isPartial && ` Remaining ${totalDispatched - totalShipping} units will stay in Pending Ship.`}
                  </Typography>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShipOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submitShip} variant="contained" startIcon={<ShipIcon />} disabled={saving}>
            {saving ? 'Shipping…' : 'Confirm Ship'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={doneOpen} onClose={() => !doneSaving && setDoneOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Remove {(doneTarget?.order_id || '').replace(/^CIN7-/, '')} from Pending Ship</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This takes the order out of the Pending Ship queue — use it when you've shipped what you're going to ship and are <strong>not</strong> shipping the remaining line(s).
          </Typography>
          <Typography variant="body2" color="textSecondary">
            The order stays marked <em>partially shipped</em> and its records are unchanged — <strong>no inventory is moved</strong> for the un-shipped lines. You can put it back any time via <strong>Restore</strong> in the Shipped tab.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDoneOpen(false)} disabled={doneSaving}>Cancel</Button>
          <Button onClick={submitDone} variant="contained" color="success" disabled={doneSaving}
            startIcon={doneSaving ? <CircularProgress size={16} /> : <DoneIcon />}>
            {doneSaving ? 'Removing…' : 'Remove from queue'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={voidOpen} onClose={() => setVoidOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Void {(voidTarget?.order_id || '').replace(/^CIN7-/, '')}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {voidTarget && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {['shipped', 'partially_shipped'].includes(voidTarget.fulfillment_status) ? (
                  <>This order has been shipped — voiding will <strong>reverse every shipment in sequence</strong> (restoring inventory and locking the fulfillments as reversed) then mark the order voided. If you only want to redo one shipment, cancel this dialog and use <strong>Unship</strong> instead.</>
                ) : (
                  <>Voiding releases the inventory reservation and marks the order cancelled. The row stays in history with a "voided" tag. If you void by mistake, you can Restore the order from the Voided view (subject to stock still being available). A reason is required for the audit trail.</>
                )}
              </Alert>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {voidTarget.customer_name || voidTarget.customer_id} — {voidTarget.currency} {Number(voidTarget.grand_total ?? voidTarget.order_revenue).toFixed(2)} from {voidTarget.warehouse_code}
              </Typography>
              <TextField
                fullWidth multiline rows={3} autoFocus
                label="Reason *"
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="e.g. 'Customer cancelled', 'Created in error', 'Duplicate of CIN7-78041'"
                helperText="Recorded to the audit log against your user account"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidOpen(false)} disabled={voiding}>Cancel</Button>
          <Button onClick={submitVoid} variant="contained" color="error" startIcon={<VoidIcon />} disabled={voiding || !voidReason.trim()}>
            {voiding ? 'Voiding…' : 'Confirm Void'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unship dialog — reverses the LATEST shipment without voiding. */}
      <Dialog open={unshipOpen} onClose={() => !unshipping && setUnshipOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Unship {(unshipTarget?.order_id || '').replace(/^CIN7-/, '')}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {unshipTarget && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                This reverses the <strong>latest</strong> shipment for this order. Inventory restored, fulfillment marked reversed. Order moves to {unshipTarget.fulfillment_status === 'partially_shipped' ? 'partial/pending' : 'pending'} so you can re-ship or void. If the order has multiple partial shipments and you want to cancel them all, use <strong>Void</strong> instead — it loops until pending then voids.
              </Alert>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {unshipTarget.customer_name || unshipTarget.customer_id} — {unshipTarget.currency} {Number(unshipTarget.grand_total ?? unshipTarget.order_revenue).toFixed(2)} from {unshipTarget.warehouse_code}
              </Typography>
              <TextField
                fullWidth multiline rows={3} autoFocus
                label="Reason *"
                value={unshipReason}
                onChange={(e) => setUnshipReason(e.target.value)}
                placeholder="e.g. 'Wrong qty shipped — redo', 'Wrong warehouse', 'Customer changed address'"
                helperText="Recorded to the audit log against your user account"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnshipOpen(false)} disabled={unshipping}>Cancel</Button>
          <Button onClick={submitUnship} variant="contained" startIcon={<UnshipIcon />} disabled={unshipping || !unshipReason.trim()}>
            {unshipping ? 'Unshipping…' : 'Confirm Unship'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore (un-void) dialog — operator's recovery path for misclicks. */}
      <Dialog open={restoreOpen} onClose={() => setRestoreOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Restore {(restoreTarget?.order_id || '').replace(/^CIN7-/, '')}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {restoreTarget && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Restoring re-places the inventory hold and returns this order to Pending Ship. If stock has been allocated elsewhere since the void, the restore will fail with a specific shortage — free up stock first or recreate the order. A reason is required for the audit trail.
              </Alert>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {restoreTarget.customer_name || restoreTarget.customer_id} — {restoreTarget.currency} {Number(restoreTarget.grand_total ?? restoreTarget.order_revenue).toFixed(2)} from {restoreTarget.warehouse_code}
              </Typography>
              <TextField
                fullWidth multiline rows={3} autoFocus
                label="Reason *"
                value={restoreReason}
                onChange={(e) => setRestoreReason(e.target.value)}
                placeholder="e.g. 'Voided by mistake', 'Customer reactivated', 'Reverse of audit reclassification'"
                helperText="Recorded to the audit log against your user account"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreOpen(false)} disabled={restoring}>Cancel</Button>
          <Button onClick={submitRestore} variant="contained" disabled={restoring || !restoreReason.trim()}>
            {restoring ? 'Restoring…' : 'Confirm Restore'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Consolidated "Fulfillment" view — one place for both queues as sub-tabs
// (To Ship reuses the Pending-Ship queue above; To Invoice reuses the existing
// Ready-to-Invoice component verbatim, so the underlying logic is unchanged).
// Mounted at the existing /wholesale/pending-ship route for testing; the final
// nav swap (two tabs -> one) needs App.js/Layout, which are out of this scope.
export default function PendingShip() {
  const [fulfillTab, setFulfillTab] = useState(0);
  return (
    <Box>
      <Tabs
        value={fulfillTab}
        onChange={(_, v) => setFulfillTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="To Ship" />
        <Tab label="To Invoice" />
      </Tabs>
      {fulfillTab === 0 ? <PendingShipQueue /> : <ReadyToInvoice />}
    </Box>
  );
}
